/**
 * Setup Progress Widget — top of Control Dashboard
 * Shows the 5-step setup flow as a horizontal progress bar with check states.
 * Click any step → navigates to that section (if not locked).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Wifi, Building2, Cpu, Send, Activity, CheckCircle2, Circle, Lock
} from 'lucide-react';
import { useControlTheme } from '@/contexts/ControlThemeContext';
import { getBearerToken } from '@/context/TrackOticAuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SetupProgressWidget() {
  const navigate = useNavigate();
  const { isDark } = useControlTheme();
  const [state, setState] = useState({
    devices: 0,
    customers: 0,
    assigned: 0,
    dispatched: 0,
    loaded: false,
  });

  useEffect(() => {
    const token = getBearerToken();
    const headers = { Authorization: `Bearer ${token}` };
    let cancelled = false;

    const load = async () => {
      try {
        const safeJson = async (url) => {
          try {
            const r = await fetch(url, { headers });
            if (!r.ok) return [];
            return await r.json();
          } catch (_) { return []; }
        };
        const [unassigned, clients, dispatched] = await Promise.all([
          safeJson(`${API_BASE}/api/v1/control/onboarding/unassigned-devices`),
          safeJson(`${API_BASE}/api/v1/control/onboarding/clients`),
          safeJson(`${API_BASE}/api/v1/control/onboarding/dispatched-devices`).catch(() => []),
        ]);
        if (cancelled) return;
        setState({
          devices: Array.isArray(unassigned) ? unassigned.length : 0,
          customers: Array.isArray(clients) ? clients.length : 0,
          // Assigned to a customer = total registered devices that are no longer "unassigned"
          // We approximate via dispatched count; if endpoint missing, fall back to 0.
          assigned: Array.isArray(dispatched) ? dispatched.length : 0,
          dispatched: Array.isArray(dispatched) ? dispatched.length : 0,
          loaded: true,
        });
      } catch (_e) { /* silent */ }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const steps = [
    {
      key: 'devices',
      label: 'Devices Registered',
      icon: Wifi,
      done: state.devices > 0 || state.assigned > 0,
      count: state.devices + state.assigned,
      path: '/control/devices',
      locked: false,
      tip: 'Register IMEIs received from manufacturer',
    },
    {
      key: 'customers',
      label: 'Customers Added',
      icon: Building2,
      done: state.customers > 0,
      count: state.customers,
      path: '/control/tenants',
      locked: false,
      tip: 'Register customer companies + auto-create admin',
    },
    {
      key: 'onboarding',
      label: 'Onboarding Started',
      icon: Cpu,
      done: state.devices > 0 && state.customers > 0,
      count: null,
      path: '/control/onboarding',
      locked: false,
      tip: 'Run the 5-step wizard to bind device → customer',
    },
    {
      key: 'dispatch',
      label: 'Dispatched',
      icon: Send,
      done: state.dispatched > 0,
      count: state.dispatched,
      path: '/control/device-dispatch',
      locked: false,
      tip: 'Send registered devices to customers',
    },
    {
      key: 'live',
      label: 'Live Operations',
      icon: Activity,
      done: state.dispatched > 0,
      count: null,
      path: '/control/cold-chain',
      locked: false,
      tip: 'Cold-chain monitoring + alerts',
    },
  ];

  const totalDone = steps.filter(s => s.done).length;
  const pct = Math.round((totalDone / steps.length) * 100);

  const cardBg = isDark ? 'bg-slate-800' : 'bg-white';
  const border = isDark ? 'border-slate-700' : 'border-slate-200';
  const textPrimary = isDark ? 'text-slate-100' : 'text-slate-900';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <Card className={cn(cardBg, border, "p-5 mb-6")} data-testid="setup-progress-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className={cn("text-base font-semibold", textPrimary)}>Setup Progress</h2>
          <p className={cn("text-xs", textMuted)}>
            {totalDone === steps.length
              ? '🎉 Setup complete — your platform is fully operational.'
              : `${totalDone} of ${steps.length} setup milestones complete`}
          </p>
        </div>
        <div className={cn("text-2xl font-bold tabular-nums", pct === 100 ? 'text-emerald-500' : 'text-blue-500')}>
          {pct}%
        </div>
      </div>

      {/* Progress bar */}
      <div className={cn("h-1.5 rounded-full overflow-hidden mb-5", isDark ? 'bg-slate-700' : 'bg-slate-200')}>
        <div
          className={cn("h-full transition-all duration-500", pct === 100 ? 'bg-emerald-500' : 'bg-blue-500')}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Step pills */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => !s.locked && navigate(s.path)}
              disabled={s.locked}
              title={s.locked ? 'Complete prior setup steps first' : s.tip}
              data-testid={`setup-step-${s.key}`}
              className={cn(
                "flex flex-col items-start p-3 rounded-lg border text-left transition-all",
                s.done
                  ? (isDark ? "bg-emerald-900/20 border-emerald-700 hover:bg-emerald-900/30" : "bg-emerald-50 border-emerald-200 hover:bg-emerald-100")
                  : s.locked
                    ? (isDark ? "bg-slate-900 border-slate-700 opacity-50 cursor-not-allowed" : "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed")
                    : (isDark ? "bg-slate-900 border-slate-700 hover:bg-slate-800" : "bg-slate-50 border-slate-200 hover:bg-slate-100")
              )}
            >
              <div className="flex items-center justify-between w-full mb-2">
                <Icon className={cn(
                  "w-5 h-5",
                  s.done
                    ? "text-emerald-500"
                    : (isDark ? "text-slate-400" : "text-slate-500")
                )} />
                {s.done ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : s.locked ? (
                  <Lock className={cn("w-3.5 h-3.5", isDark ? "text-slate-500" : "text-slate-400")} />
                ) : (
                  <Circle className={cn("w-3.5 h-3.5", isDark ? "text-slate-500" : "text-slate-400")} />
                )}
              </div>
              <p className={cn("text-[10px] uppercase tracking-wider font-semibold", textMuted)}>Step {i + 1}</p>
              <p className={cn("text-sm font-medium leading-tight", textPrimary)}>{s.label}</p>
              {s.count !== null && (
                <p className={cn("text-xs mt-1", s.done ? "text-emerald-600 dark:text-emerald-400" : textMuted)}>
                  {s.count > 0 ? `${s.count} active` : 'Pending'}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
